import React, { useCallback, useContext, useEffect } from 'react';
import { InstagramPostTypeBlockView, InstagramPostTypeBlockViewProps } from './InstagramPostTypeBlock.view';
import { PostEditorContext } from '../states/PostEditorProvider';
import { PostEditorGroupDataContext } from '../states/PostEditorGroupDataProvider';
import { ActiveCustomizeIndexContext } from '../states/ActiveCustomizeIndexProvider';
import { FormElementAction, FormElementValueType } from '../../../models/basic-objects';
import { MediaType, PostFeedType, PostPublishMethod } from '../../../models/graphql';
import { MaxIgVideoLengthValue } from '../../../constants/media';
import { SocialProfileWithBoardsAndMentionType } from '../helpers/mapSelectedProfilesValues';

export type InstagramPostTypeBlockControllerProps = {
  // extend your InstagramPostTypeBlockViewProps
  formInstance: InstagramPostTypeBlockViewProps['formInstance'];
};

export type IgProfileType = 'personal' | 'mixed' | 'business' | 'creator' | null;

export const IgProfilesTypeFn = (igProfiles: SocialProfileWithBoardsAndMentionType[]) =>
  igProfiles.reduce((prev: 'business' | 'creator' | 'personal' | 'mixed' | null | undefined, curr) => {
    if (curr.__typename === 'GenericSocialProfile' && curr.genericProfile === 'INSTAGRAM_PERSONAL') return 'personal';
    if (prev === null || curr.__typename !== 'InstagramSocialProfile') return null;
    if (
      prev === 'mixed' ||
      ((curr.type === 'business' || curr.type === 'media_creator') && prev === 'personal') ||
      (curr.type === 'personal' && prev === 'business') ||
      (curr.type === 'personal' && prev === 'creator')
    ) {
      return 'mixed';
    }
    if (curr.type === 'business') {
      return 'business';
    }
    if (curr.type === 'media_creator') {
      return 'creator';
    }
    return 'personal';
  }, undefined);

export const InstagramPostTypeBlockController = (props: InstagramPostTypeBlockControllerProps): JSX.Element => {
  // inner consts
  // Put All your methods and logic here
  const { formInstance } = props;
  const { setValue } = formInstance;
  const { state, dispatch, profiles } = useContext(PostEditorContext);
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const { postGroups } = state;
  const postGroup = postGroups[postGroupIndex];
  const { customizeContentPerProfile, posts, profiles: selectedBasicProfiles } = postGroup;
  const profileId =
    customizeContentPerProfile && typeof customizeIndex === 'number' ? posts[customizeIndex].customizedProfileId : null;
  const selectedProfilesIds = selectedBasicProfiles.map(p => p.profileId);

  const igProfiles =
    customizeContentPerProfile && typeof customizeIndex === 'number'
      ? profiles.filter(p => p.__typename === 'InstagramSocialProfile' && p.id === profileId)
      : profiles.filter(
          profile => profile.__typename === 'InstagramSocialProfile' && selectedProfilesIds.includes(profile.id)
        );
  const igProfilesType = IgProfilesTypeFn(igProfiles);

  const hasIgProfile =
    customizeContentPerProfile && typeof customizeIndex === 'number'
      ? profileId && !!igProfiles.find(profile => profile.id === profileId)
      : igProfiles.length;

  const directPublishing =
    customizeContentPerProfile && typeof customizeIndex === 'number'
      ? !!profiles.find(p => p.__typename === 'InstagramSocialProfile' && p.directPublishEnabled && p.id === profileId)
      : !!igProfiles.filter(p => p.__typename === 'InstagramSocialProfile' && p.directPublishEnabled).length;

  const igType =
    customizeContentPerProfile && typeof customizeIndex === 'number'
      ? posts[customizeIndex].content.postFeedType
      : postGroup.groupPostAttributes.content.postFeedType;
  const igPublishMethod =
    customizeContentPerProfile && typeof customizeIndex === 'number'
      ? posts[customizeIndex].content.publishMethod
      : postGroup.groupPostAttributes.content.publishMethod;
  const igTypeName =
    customizeContentPerProfile && typeof customizeIndex === 'number'
      ? `postGroup.${postGroupIndex}.customize.${customizeIndex}.postFeedType`
      : `postGroup.${postGroupIndex}.postFeedType`;
  const igPublishMethodName =
    customizeContentPerProfile && typeof customizeIndex === 'number'
      ? `postGroup.${postGroupIndex}.customize.${customizeIndex}.igPublishMethod`
      : `postGroup.${postGroupIndex}.igPublishMethod`;

  const media =
    customizeContentPerProfile && typeof customizeIndex === 'number'
      ? posts[customizeIndex].content.media
      : postGroup.groupPostAttributes.content.media;

  const videos = media.filter(m => m.type === MediaType.VIDEO);

  const maxVideoLength = Math.max(...videos.map(v => v.length || 0));
  const hasLongVideo = maxVideoLength >= MaxIgVideoLengthValue && igType === PostFeedType.FEED;

  const isPostFeedTypeGuard = (value: FormElementValueType): value is PostFeedType => {
    if (typeof value !== 'string') throw new Error('value is not of type string');
    return value in PostFeedType;
  };
  const updateIgTypeAction: FormElementAction = (value, complete) => {
    if (!isPostFeedTypeGuard(value)) throw new Error('Value is not a proper PostFeedType enum');
    complete();
    dispatch({
      type: 'setPostFeedType',
      payload: {
        postFeedType: value,
        postGroupIndex,
        customizeIndex
      }
    });
  };

  const isPublishMethodGuard = (value: FormElementValueType): value is PostPublishMethod => {
    if (typeof value !== 'string') throw new Error('value is not of type string or null');
    return value in PostPublishMethod;
  };

  const updateIgPublishMethodAction: FormElementAction = useCallback(
    (value, complete) => {
      if (!isPublishMethodGuard(value)) throw new Error('Value is not a proper PostFeedType enum');
      complete();
      dispatch({
        type: 'setPublishMethod',
        payload: {
          publishMethod: value,
          postGroupIndex,
          customizeIndex
        }
      });
    },
    [customizeIndex, dispatch, postGroupIndex]
  );

  // set the default publishing method
  useEffect(() => {
    if (customizeContentPerProfile && typeof customizeIndex !== 'number') return;
    if (!hasIgProfile && igPublishMethod) {
      dispatch({
        type: 'setPublishMethod',
        payload: {
          publishMethod: null,
          postGroupIndex,
          customizeIndex
        }
      });
    } else if (!igPublishMethod && hasIgProfile) {
      dispatch({
        type: 'setPublishMethod',
        payload: {
          publishMethod: directPublishing ? PostPublishMethod.DIRECT : PostPublishMethod.MANUAL,
          postGroupIndex,
          customizeIndex
        }
      });
    }
  }, [
    customizeContentPerProfile,
    customizeIndex,
    directPublishing,
    dispatch,
    hasIgProfile,
    hasLongVideo,
    igPublishMethod,
    postGroupIndex,
    setValue
  ]);

  const viewProps: InstagramPostTypeBlockViewProps = {
    ...props,
    updateIgTypeAction,
    updateIgPublishMethodAction,
    igType,
    igPublishMethod,
    igTypeName,
    igPublishMethodName,
    igProfilesType
  };

  return hasIgProfile ? <InstagramPostTypeBlockView {...viewProps} /> : <></>;
};
